/**
 * useTableSort — small hook + <SortableTh> helper for admin tables.
 *
 * Usage :
 *   const { sorted, sortKey, sortDir, toggleSort } =
 *     useTableSort(rows, { initialKey: 'created_at', initialDir: 'desc' });
 *   ...
 *   <SortableTh sortKey="email" current={sortKey} dir={sortDir} onSort={toggleSort}>
 *     Email
 *   </SortableTh>
 *
 * The hook does client-side sort on whatever array is passed in. For
 * server-paginated data, prefer pushing the sort to the API call —
 * this is purposely simple and ideal for the admin SPA where pages
 * already fetch full result sets (≤500 rows).
 */

import { ChevronDown, ChevronUp, ChevronsUpDown } from 'lucide-react';
import { type ReactElement, type ReactNode, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

interface Options<T> {
  initialKey?: Extract<keyof T, string> | null;
  initialDir?: SortDir;
}

export function useTableSort<T extends object>(
  rows: T[] | undefined,
  options: Options<T> = {},
): {
  sorted: T[];
  sortKey: Extract<keyof T, string> | null;
  sortDir: SortDir;
  toggleSort: (key: Extract<keyof T, string>) => void;
} {
  type K = Extract<keyof T, string>;
  const [sortKey, setSortKey] = useState<K | null>(options.initialKey ?? null);
  const [sortDir, setSortDir] = useState<SortDir>(options.initialDir ?? 'asc');

  function toggleSort(key: K): void {
    if (sortKey === key) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDir('asc');
    }
  }

  const sorted = useMemo(() => {
    const list = rows ?? [];
    if (!sortKey) return list;
    const factor = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      const av = (a as Record<string, unknown>)[sortKey];
      const bv = (b as Record<string, unknown>)[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === 'number' && typeof bv === 'number') {
        return (av - bv) * factor;
      }
      if (typeof av === 'boolean' && typeof bv === 'boolean') {
        return (av === bv ? 0 : av ? -1 : 1) * factor;
      }
      const as = String(av);
      const bs = String(bv);
      return as.localeCompare(bs, undefined, { numeric: true, sensitivity: 'base' }) * factor;
    });
  }, [rows, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

interface SortableThProps<K extends string> {
  sortKey: K;
  current: K | null;
  dir: SortDir;
  onSort: (key: K) => void;
  className?: string;
  align?: 'left' | 'right' | 'center';
  children: ReactNode;
}

export function SortableTh<K extends string>(props: SortableThProps<K>): ReactElement {
  const { sortKey, current, dir, onSort, className = '', align = 'left', children } = props;
  const isActive = current === sortKey;
  const Icon = isActive ? (dir === 'asc' ? ChevronUp : ChevronDown) : ChevronsUpDown;
  const alignClass =
    align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left';
  return (
    <th className={`px-3 py-2 ${alignClass} ${className}`}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={`inline-flex items-center gap-1 font-medium uppercase tracking-wide text-[11px] transition-colors ${
          isActive ? 'text-foreground' : 'text-muted-foreground hover:text-foreground'
        }`}
      >
        {children}
        <Icon className={`h-3 w-3 ${isActive ? 'opacity-100' : 'opacity-50'}`} aria-hidden="true" />
      </button>
    </th>
  );
}
